/**
 * Oath BSMS Report Service
 * API calls for BSMS Crystal Reports - proxies report generation requests
 * to the backend which communicates with the external BSMS Crystal Reports server.
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

// ============================================
// AGENT DROPDOWN
// ============================================

/**
 * Get available agents for the current user
 * Returns key-value pairs (Agent Name -> Agent ID)
 */
export const getAvailableAgents = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/bsms/agents`);
    if (!response.ok) {
      return { success: false, error: 'Failed to load agents' };
    }
    const data = await response.json().catch(() => ({}));
    const result = data.result !== undefined ? data.result : data;
    return { success: true, data: result };
  } catch (error) {
    console.error('Get available agents error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// REPORT GENERATION
// ============================================

/**
 * Generate a BSMS Crystal Report PDF
 * @param {Object} params - Report parameters
 * @param {string} params.reportName - Crystal Report file name
 * @param {number} params.paramSet - Parameter set ID
 * @param {Object} params.additionalParams - Report-specific parameters (includes agentId)
 * @returns {Promise<{success: boolean, data?: Blob, error?: string}>}
 */
export const generateBsmsReport = async ({ reportName, paramSet, additionalParams = {} }) => {
  try {
    // Build a clean params object - additionalParams already contains agentId
    const body = {
      reportName,
      paramSet: Number(paramSet),
      ...additionalParams,
    };

    const response = await authFetch(`${BASE_URL}oath/bsms/report`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Accept': 'application/pdf',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || 'Failed to generate report' };
    }

    const blob = await response.blob();
    return { success: true, data: blob };
  } catch (error) {
    console.error('Generate BSMS report error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// REPORT DEFINITIONS
// ============================================

/**
 * Report definitions with their parameter configurations
 */
export const BSMS_REPORTS = {
  AGENCY_VITAL_STATISTICS: {
    id: 'agency-vital-statistics',
    title: 'Agency Vital Statistics Report Card',
    reportName: 'Agency_Vital_Statistics_Report_Card.rpt',
    paramSet: 1,
    generalAgentOnly: true,
  },
  AGENT_STATEMENT_PACKAGE: {
    id: 'agent-statement-package',
    title: 'Agent Statement Package',
    reportName: 'Agent+Statement+Package.rdl',
    paramSet: 418,
    generalAgentOnly: true,
  },
  AGENT_OPEN_LIABILITY: {
    id: 'agent-open-liability',
    title: 'Agent Open Liability Details',
    reportName: 'Agent+Open+Liability+Details.rdl',
    paramSet: 416,
    generalAgentOnly: false,
  },
  AGENT_UNEXECUTED_DETAIL: {
    id: 'agent-unexecuted-detail',
    title: 'Agent Unexecuted Detail',
    reportName: 'Agent+Unexecuted+Detail.rdl',
    paramSet: 417,
    generalAgentOnly: false,
  },
  EXPIRED_UNEXECUTED_DETAIL: {
    id: 'expired-unexecuted-detail',
    title: 'Expired Unexecuted Detail by Agent',
    reportName: 'ExpiredUnexecutedDetailbyAgt.rpt',
    paramSet: 6,
    generalAgentOnly: false,
  },
  FORFEITURE: {
    id: 'forfeiture',
    title: 'Forfeiture Reports',
    reportName: 'Agent%20Forfeiture-Judgement%20Log.rdl',
    paramSet: 415,
    generalAgentOnly: false,
  },
};

/**
 * Sort options for Agent Open Liability Details
 */
export const OPEN_LIABILITY_SORT_OPTIONS = [
  { value: 'Type', label: 'Type' },
  { value: 'PowerNo', label: 'Power No' },
  { value: 'Liability', label: 'Liability' },
  { value: 'ExecuteDate', label: 'Execute Date' },
  { value: 'Defendant', label: 'Defendant' },
  { value: 'City', label: 'City' },
  { value: 'County', label: 'County' },
  { value: 'CaseNo', label: 'Case No' },
];

/**
 * Sort options for Agent Unexecuted Detail
 */
export const UNEXECUTED_SORT_OPTIONS = [
  { value: 'PowerNo', label: 'Power No' },
  { value: 'Company', label: 'Company' },
  { value: 'Type', label: 'Type' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Status', label: 'Status' },
];

/**
 * Sort options for Forfeiture report.
 * Values must match BSMS Crystal Report's ddlFLogSortBy dropdown exactly —
 * ASP.NET event validation rejects unknown values.
 */
export const FORFEITURE_SORT_OPTIONS = [
  { value: 'FJDue', label: 'F-J Due Date' },
  { value: 'FJDate', label: 'F-J Date' },
  { value: 'PowerNo', label: 'Power No' },
  { value: 'ExDate', label: 'Execution Date' },
  { value: 'Rcvd', label: 'Received Date' },
  { value: 'FJAmt', label: 'F-J Amount' },
  { value: 'Defendant', label: 'Defendant Name' },
  { value: 'CaseNum', label: 'Case Number' },
  { value: 'County', label: 'County' },
  { value: 'ST', label: 'State' },
  { value: 'Status', label: 'Status' },
  { value: 'TBond', label: 'Transfer Bond' },
];

/**
 * Sort direction options
 */
export const SORT_DIRECTIONS = [
  { value: 'ASC', label: 'Ascending' },
  { value: 'DESC', label: 'Descending' },
];
